import { useEffect, useRef, useState } from 'react';
import { Card, Button, Badge, Textarea } from '../../../shared/components';
import { MyDay, RecordType, RECORD_TYPE_LABEL } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';
import { DAY_NAMES, formatClock, shortTime } from '../jornadaUtil';
import { useLabels } from '../labels';

const MARK_ORDER: RecordType[] = ['ENTRADA', 'SALIDA_ALMUERZO', 'ENTRADA_ALMUERZO', 'SALIDA'];

const ENABLED_HELP: Record<RecordType, string> = {
  ENTRADA: 'Márcala al llegar a trabajar. Inicia el reloj de tu jornada de hoy.',
  SALIDA_ALMUERZO: 'Márcala cuando salgas a almorzar. El reloj se detiene hasta que marques el regreso.',
  ENTRADA_ALMUERZO: 'Márcala cuando vuelvas del almuerzo. El reloj sigue corriendo.',
  SALIDA: 'Márcala al terminar tu jornada. Cierra el reloj del día.',
};

/** Por qué una marcación no está disponible ahora (se muestra al pasar el mouse / mantener presionado). */
function disabledReason(type: RecordType, state: MyDay['state'], stale: boolean): string {
  if (stale) return 'Primero cierra tu turno anterior (aviso rojo de arriba).';
  const open = state === 'TRABAJANDO' || state === 'EN_ALMUERZO' || state === 'ALMUERZO_TERMINADO';
  switch (type) {
    case 'ENTRADA':
      return 'Ya marcaste tu entrada. Cuando termines tu jornada marca la salida.';
    case 'SALIDA_ALMUERZO':
      return open ? 'Ya marcaste tu salida a almorzar.' : 'Primero marca tu entrada.';
    case 'ENTRADA_ALMUERZO':
      return state === 'ALMUERZO_TERMINADO' ? 'Ya marcaste el regreso del almuerzo.' : open ? 'Primero marca la salida a almorzar.' : 'Primero marca tu entrada.';
    case 'SALIDA':
      return state === 'EN_ALMUERZO' ? 'Estás en tu almuerzo: marca primero el regreso del almuerzo.' : 'No tienes un turno abierto. Marca tu entrada primero.';
  }
}

interface Props {
  day: MyDay | null;
  /** Instante (performance.now) en que se recibio `day`, para que el reloj avance sin pedirle nada al servidor cada segundo. */
  receivedAt: number;
  isSubmitting: boolean;
  note: string;
  onNoteChange: (v: string) => void;
  onMark: (type: RecordType) => void;
}

/** Tarjeta "Mi jornada de hoy": reloj del dia, horario, las 4 marcaciones y los botones habilitados segun la secuencia. */
export function JornadaCard({ day, receivedAt, isSubmitting, note, onNoteChange, onMark }: Props) {
  const [, setTick] = useState(0);
  const [showNote, setShowNote] = useState(false);
  const labels = useLabels();
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // Un tick por segundo solo mientras hay algo que mover (reloj o almuerzo en curso).
  const moving = !!day?.clock && (day.clock.running || day.clock.lunchInProgress);
  useEffect(() => {
    if (!moving) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [moving]);
  useEffect(() => {
    if (showNote) noteRef.current?.focus();
  }, [showNote]);

  if (!day) {
    return (
      <Card>
        <p className="text-sm text-ink-400">Consultando tu jornada…</p>
      </Card>
    );
  }

  const elapsed = (performance.now() - receivedAt) / 1000;
  const clock = day.clock;
  const worked = clock ? clock.workedSeconds + (clock.running ? elapsed : 0) : 0;
  const lunchLive = clock?.lunchInProgress ? (clock.lunchSeconds ?? 0) + elapsed : clock?.lunchSeconds ?? null;
  const stale = day.openShift?.stale === true;
  const byType = new Map(day.marks.map((m) => [m.type, m]));
  const nextType = day.allowedNext[0];
  const s = day.schedule;

  const status = !clock
    ? { tone: 'neutral' as const, label: 'Sin turno abierto' }
    : clock.closed
      ? { tone: 'info' as const, label: 'Jornada cerrada' }
      : clock.lunchInProgress
        ? { tone: 'warning' as const, label: 'En almuerzo · reloj detenido' }
        : { tone: 'success' as const, label: 'Reloj corriendo' };

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
          Mi jornada de hoy
          <InfoTip
            title="¿Cómo funciona el reloj?"
            text={labels.t('AYUDA_RELOJ')}
          />
        </p>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>

      {day.showClock !== false && (
      <div className="mt-3 rounded-2xl bg-ink-50 py-5 text-center">
        <p className={['font-mono text-4xl font-bold tabular-nums tracking-tight', clock?.running ? 'text-brand-700' : 'text-ink-700'].join(' ')} aria-live="off">
          {formatClock(worked)}
        </p>
        <p className="mt-1 text-xs text-ink-400">
          {!clock && 'Marca tu entrada para iniciar el reloj'}
          {clock && clock.closed && 'Total trabajado hoy (sin el almuerzo)'}
          {clock && !clock.closed && !clock.lunchInProgress && 'Tiempo trabajado hoy'}
          {clock && clock.lunchInProgress && `Llevas ${formatClock(lunchLive ?? 0)} de almuerzo`}
        </p>
        {clock?.closed && clock.lunchAutoDiscount && (
          <p className="mx-4 mt-2 text-[11px] text-ink-400">No marcaste el almuerzo completo: el sistema descuenta el tiempo de almuerzo de tu jornada.</p>
        )}
      </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ink-500">
        <span className="break-words">
          {labels.nodes('JORNADA_LINEA', { dia: DAY_NAMES[day.dayOfWeek], inicio: s.start, fin: s.end, almuerzo_inicio: s.lunchStart, almuerzo_fin: s.lunchEnd, almuerzo_sugerido: labels.t('ALMUERZO_SUGERIDO') }, ['inicio', 'fin'])}
        </span>
        {s.source === 'EXCEPCION' && (
          <span className="inline-flex items-center gap-1">
            <Badge tone="info">Horario especial</Badge>
            <InfoTip title="Horario de excepción" text={s.exceptionReason ?? 'Tienes un horario distinto al general.'} />
          </span>
        )}
        {!s.isWorkDay && <Badge tone="warning">Hoy no es día laborado</Badge>}
      </div>

      {/* Las 4 marcaciones del dia */}
      <ol className="mt-4 grid grid-cols-2 gap-2">
        {MARK_ORDER.map((type) => {
          const mark = byType.get(type);
          const isNext = !stale && day.allowedNext.includes(type) && type === nextType;
          return (
            <li
              key={type}
              className={['rounded-xl border px-3 py-2', mark ? 'border-brand-200 bg-brand-light' : isNext ? 'border-brand-300 bg-surface' : 'border-ink-100 bg-surface'].join(' ')}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-ink-400">{RECORD_TYPE_LABEL[type]}</p>
              <p className={['text-base font-semibold tabular-nums', mark ? 'text-brand-700' : 'text-ink-300'].join(' ')}>
                {mark ? shortTime(mark.at, day.timeZone) : '— : —'}
                {mark?.isLateClose && <span className="ml-1 text-[10px] font-medium text-warning">cierre tardío</span>}
                {mark?.isCorrected && <span className="ml-1 text-[10px] font-medium text-info">corregida</span>}
              </p>
            </li>
          );
        })}
      </ol>

      <p className="mt-3 rounded-xl bg-blue-50 p-3 text-xs leading-relaxed text-info">
        Cuando sales a almorzar marca la salida a almorzar. Tú, como empleado, detén el reloj en mitad de jornada y, una vez ingreses, debes marcar la entrada de almuerzo.
      </p>

      {/* Botones: solo la accion que corresponde esta habilitada */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {MARK_ORDER.map((type) => {
          const enabled = !stale && day.allowedNext.includes(type);
          const primary = enabled && type === nextType;
          return (
            <div key={type} title={enabled ? ENABLED_HELP[type] : disabledReason(type, day.state, stale)} className="flex">
              <Button
                size="lg"
                fullWidth
                variant={primary ? 'primary' : enabled ? 'secondary' : 'outline'}
                disabled={!enabled || isSubmitting}
                isLoading={isSubmitting && enabled && primary}
                onClick={() => onMark(type)}
                className={enabled ? '' : 'opacity-50'}
              >
                {RECORD_TYPE_LABEL[type]}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-center text-[11px] text-ink-400">Pasa el mouse (o mantén presionado) sobre un botón para ver qué hace o por qué no está disponible.</p>

      {/* Nota opcional */}
      <div className="mt-3">
        {!showNote && !note ? (
          <button type="button" className="text-xs font-medium text-brand-700 underline" onClick={() => setShowNote(true)}>
            + Agregar una nota a esta marcación (opcional)
          </button>
        ) : (
          <Textarea
            ref={noteRef}
            label="Nota (opcional)"
            value={note}
            maxLength={300}
            rows={2}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Ej.: llegué tarde por un bloqueo en la vía"
            hint={`${note.length}/300 · Queda guardada junto con tu marcación.`}
          />
        )}
      </div>
    </Card>
  );
}
